"use client";
import { useFormik } from "formik";
import { FiSend } from "react-icons/fi";
import React, { useEffect, useState } from "react";
import { trackLead } from "src/utils/func/trackLead";
import { countries } from "src/utils/mock/countryCode";
import PrimaryButton from "src/components/atoms/PrimaryButton";
import { services, validationSchema } from "src/utils/mock/contactValidation";

const ContactForm = ({ sent, setSent }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(true);

  const formik = useFormik({
    initialValues: {
      full_name: "",
      email_address: "",
      phone_number: "",
      services: "",
      message: "",
      country_code: "+91",
      budget: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);

      const formData = new URLSearchParams({
        name: values.full_name.trim(),
        email: values.email_address.trim(),
        phone: values.phone_number.trim(),
        our_service: values.services,
        message: values.message.trim(),
        resume: "",
        country_code: values.country_code,
        budget: values.budget,
      });

      try {
        const response = await fetch("https://api-tycho.digitalseller.in/contact-us", {
          method: "PUT",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        });

        const res = await response.json();

        if (res?.status === true) {
          trackLead({
            formName: "Contact Page Form",
            service: values.services,
          });

          setSent(true);
          formik.resetForm();
          setTimeout(() => setSent(false), 5000);
        } else {
          alert(res?.message || "Something went wrong. Please try again.");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        alert("Failed to send message. Please check your internet connection.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const detectCountry = async () => {
      setIsGeoLoading(true);

      try {
        const res = await fetch("https://geolocation-db.com/json/", { cache: "no-store" });
        const data = await res.json();

        if (data.country_code) {
          const matched = countries.find((c) => c.short_name.toUpperCase() === data.country_code.toUpperCase());
          if (matched && matched.phone_code) {
            const code = `+${matched.phone_code}`;
            formik.setFieldValue("country_code", code);
            setIsGeoLoading(false);
            return;
          }
        }
      } catch (e) {
        console.warn("Primary geolocation failed");
      }

      try {
        const fallbackRes = await fetch("https://ip-api.com/json");
        const fallbackData = await fallbackRes.json();

        if (fallbackData.countryCode) {
          const matched = countries.find((c) => c.short_name.toUpperCase() === fallbackData.countryCode.toUpperCase());
          if (matched && matched.phone_code) {
            const code = `+${matched.phone_code}`;
            formik.setFieldValue("country_code", code);
          }
        }
      } catch (err) {
        console.error("Geolocation fallback also failed");
      } finally {
        setIsGeoLoading(false);
      }
    };

    detectCountry();
  }, []);

  return (
    <div>
      <form onSubmit={formik.handleSubmit} className="contact-form" noValidate>
        <div className="form-header">
          <h3 className="form-title">Tell Us About Your Project</h3>
          {/* <p className="form-sub">  We'll get back to you within 24 hours</p> */}
        </div>

        <div className="fields-grid">
          <div className={`field-wrap${formik.touched.full_name && formik.errors.full_name ? " field-error" : ""}`}>
            <label className="" htmlFor="full_name">
              Full name <span className="text-red-600">*</span>
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              placeholder="Full Name"
              value={formik.values.full_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="field-input"
            />
            {formik.touched.full_name && formik.errors.full_name && <div className="text-red-500 text-xs mt-1">{formik.errors.full_name}</div>}
            {/* <span className="field-line" /> */}
          </div>

          <div className={`field-wrap${formik.touched.email_address && formik.errors.email_address ? " field-error" : ""}`}>
            <label className="" htmlFor="email_address">
              Email address <span className="text-red-600">*</span>
            </label>
            <input
              id="email_address"
              name="email_address"
              type="email"
              placeholder="demo@company.com"
              value={formik.values.email_address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="field-input"
            />
            {formik.touched.email_address && formik.errors.email_address && <div className="text-red-500 text-xs mt-1">{formik.errors.email_address}</div>}
            {/* <span className="field-line" /> */}
          </div>

          <div className={`field-wrap${formik.touched.phone_number && formik.errors.phone_number ? " field-error" : ""}`}>
            <label className="" htmlFor="phone_number">
              Phone number <span className="text-red-600">*</span>
            </label>
            <div className="flex gap-2">
              <select
                name="country_code"
                value={formik.values.country_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isGeoLoading}
                className="field-input text-left font-medium !w-22"
              >
                {countries
                  .filter((c) => c.phone_code && c.phone_code !== "0")
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((country) => (
                    <option key={country.id} value={`+${country.phone_code}`}>
                      +{country.phone_code} ({country.short_name})
                    </option>
                  ))}
              </select>

              <input
                id="phone_number"
                name="phone_number"
                type="tel"
                placeholder="Enter phone number"
                value={formik.values.phone_number}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  formik.setFieldValue("phone_number", onlyDigits);
                }}
                onBlur={formik.handleBlur}
                maxLength={15}
                className="field-input flex-1"
              />
            </div>
            {formik.touched.phone_number && formik.errors.phone_number && <div className="text-red-500 text-xs mt-1">{formik.errors.phone_number}</div>}
            {/* <span className="field-line" /> */}
          </div>

          <div className={`field-wrap${formik.touched.services && formik.errors.services ? " field-error" : ""}`}>
            <label className="" htmlFor="services">
              Service needed <span className="text-red-600">*</span>
            </label>
            <select id="services" name="services" value={formik.values.services} onChange={formik.handleChange} onBlur={formik.handleBlur} className="field-input field-select">
              {services.map((s) => (
                <option key={s.value || `service-${s.label}`} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            {formik.touched.services && formik.errors.services && <div className="text-red-500 text-xs mt-1">{formik.errors.services}</div>}
            {/* <span className="field-line" /> */}
          </div>
          <div className={`field-wrap`}>
            <label className="" htmlFor="budget">
              Estimated Budget (Optional)
            </label>
            <input
              id="budget"
              name="budget"
              type="text"
              placeholder="e.g. $5,000 – $10,000"
              value={formik.values.budget}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="field-input"
            />
          </div>
        </div>

        <div className={`field-wrap field-wrap-full${formik.touched.message && formik.errors.message ? " field-error" : ""}`}>
          <label className="" htmlFor="message">
            Project details <span className="text-red-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            placeholder="Tell us about your goals, timeline, and budget…"
            value={formik.values.message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={5}
            className="field-input field-textarea"
          />
          {formik.touched.message && formik.errors.message && <div className="text-red-500 text-xs mt-1">{formik.errors.message}</div>}
          {/* <span className="field-line" /> */}
        </div>
        <div className="flex justify-center">
          <PrimaryButton size="lg" type="submit" icon={<FiSend />} className="!px-5 !py-3  " loading={isLoading}>
            Discuss Your Project
          </PrimaryButton>
        </div>
        {/* <button type="submit" disabled={isLoading} className="submit-btn disabled:opacity-70 disabled:cursor-not-allowed">
          <span className="submit-label">{isLoading ? "Sending..." : "Send message"}</span>
          <span className="submit-icon">
            <FiSend style={{ width: 16, height: 16 }} />
          </span>
        </button> */}
      </form>
      <style jsx>{`
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .form-header {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .form-title {
          font-size: 1.4rem;
          font-weight: 600;
          color: var(--text-primary);
          letter-spacing: -0.02em;
        }

        .form-sub {
          font-size: 13px;
          color: var(--c-ink-2);
        }

        .fields-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }

        @media (min-width: 560px) {
          .fields-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        .field-wrap {
          display: flex;
          flex-direction: column;
          gap: 6px;
          position: relative;
        }

        .field-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--text-secondary);
          transition: color 0.2s;
        }

        .field-input {
          width: 100%;
          padding: 13px 16px;
          background: var(--background-primary);
          border: 1px solid var(--border-primary);
          border-radius: 12px;
          font-size: 14px;
          color: var(--text-primary);
          outline: none;
          transition:
            border-color 0.2s,
            background 0.2s;
          -webkit-appearance: none;
        }

        .field-input::placeholder {
          color: #c6c7ca;
        }

        .field-input:focus {
          border-color: var(--accent);
        }

        .field-select {
          cursor: pointer;
        }

        .field-textarea {
          resize: none;
          line-height: 1.7;
        }

        .field-line {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: var(--c-brand);
          border-radius: 0 0 12px 12px;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .field-wrap.field-error .field-line {
          background: #ef4444;
        }

        .field-focused .field-line {
          transform: scaleX(1);
        }

        .field-wrap-full {
          grid-column: 1 / -1;
        }

        /* Submit Button */
        .submit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 15px 32px;
          background: var(--c-brand);
          color: #fff;
          border: none;
          border-radius: 14px;
          font-family: var(--font-display, "Syne", sans-serif);
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.02em;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition:
            opacity 0.2s,
            transform 0.18s,
            box-shadow 0.2s;
          box-shadow: 0 8px 28px var(--c-brand-glow);
        }

        .submit-btn:hover {
          opacity: 0.92;
          transform: translateY(-2px);
          box-shadow: 0 12px 36px var(--c-brand-glow);
        }

        .submit-btn:active {
          transform: scale(0.98);
        }

        .submit-icon {
          display: flex;
          align-items: center;
        }

        /* Error state for fields */
        .field-error .field-label {
          color: #ef4444;
        }

        @media (max-width: 640px) {
          .field-input {
            padding: 12px 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactForm;
